import React from "react";
import { Page, View, Text } from "@react-pdf/renderer";

import getPDFTranslations from "utils/translation/f280-pdf";
import { APP_VERSION } from "utils/constants/version";

import { styles, COLORS } from "../f280Pdf/pdfStyles";
import { Row, NumField, SmallTitle, Field, Checkbox, ValueBox, Clause } from "../f280Pdf/pdfPrimitives";
import { TitleBar, Footer, DisclaimerProjectRow } from "../f280Pdf/pdfShared";

// Page 1 of the F280 report - the "COMPLIANCE" cover sheet.
// Rebuilt as a vector react-pdf <Page> (US Letter). Data shape and translation
// keys are unchanged from the legacy raster version.
const F280FirstPage = ({
    heatLossValue,
    latHeatGainValue,
    heatUnit,
    name,
    inputFieldsData,
    displayData,
    calcPerformedBy,
    language,
    includeRoomByRoomResults,
}) => {
    const {
        issuedForTheUseOf,
        projectNo,
        lot,
        site,
        notes,
        additionalDocsList,
        designSummary,
        roomByRoomResults,
        email: emailFromForm = "",
    } = inputFieldsData;

    const { buildingLocation = {}, calculationBasedOn = {} } = displayData || {};
    const { cityProvince = "", postalCode = "", address = "" } = buildingLocation || {};
    const { units = "" } = calculationBasedOn || {};

    const {
        cityProvince: userCityProvince,
        company,
        companyAddress,
        companyFax,
        companyPhone,
        companyPostalCode,
        email,
        userName,
    } = calcPerformedBy || {};

    const { t } = getPDFTranslations(language);

    const nominal = parseFloat(latHeatGainValue);
    const totalPages = includeRoomByRoomResults ? 3 : 2;

    return (
        <Page size="LETTER" style={styles.page}>
            {/* wrap={false}: the form is designed to fill exactly one page, so keep
                it as one unbreakable block. Without this, content that lands a hair
                over the page height pushes just the footer onto a near-empty extra
                page instead of staying put. */}
            <View style={styles.body} wrap={false}>
                <TitleBar
                    title={t("f280pdf.title", "CSA STANDARD F280-12 COMPLIANCE [!!]")}
                    subtitle={t(
                        "f280pdf.subtitle",
                        "NBC 2015: 9.33.5.1.; 9.36.3.2. & 9.36.5.15; NBC 2020; 9.33.5.1.; 9.36.3.2.; 9.36.5.15 (5); 9.36.8.9. (1); [!!]"
                    )}
                    rightPrimary={t("f280pdf.standardForm.1", "CSA-F280-M12 [!!]")}
                    rightSecondary={t("f280pdf.standardForm.2", "Standard Form No. 1 [!!]")}
                />

                {/* Disclaimer + project number */}
                <DisclaimerProjectRow t={t} issuedForTheUseOf={issuedForTheUseOf} projectNo={projectNo} />

                <SmallTitle>{t("f280pdf.buildingLoc", "BUILDING LOCATION [!!]")}</SmallTitle>
                <Row>
                    <Field
                        label={t("f280pdf.model", "Model: [!!]")}
                        value={name}
                        num="3"
                        containerStyle={styles.borderRight}
                    />
                    <Field label={t("f280pdf.site", "Site: [!!]")} value={site} num="6" />
                </Row>
                <Row>
                    <Field
                        label={t("f280pdf.address", "Address: [!!]")}
                        value={address}
                        num="4"
                        containerStyle={styles.borderRight}
                    />
                    <Field label={t("f280pdf.lot", "Lot: [!!]")} value={lot} num="7" />
                </Row>
                <Row>
                    <Field
                        label={t("f280pdf.city&Province", "City & Province: [!!]")}
                        value={cityProvince}
                        num="5"
                        containerStyle={styles.borderRight}
                    />
                    <Field label={t("f280pdf.postalCode", "Postal code: [!!]")} value={postalCode} num="8" />
                </Row>

                <SmallTitle supText={t("f280pdf.seePage2", "(See page 2 for input summary) [!!]")}>
                    {t("f280pdf.compliance", "COMPLIANCE [!!]")}
                </SmallTitle>
                <Row>
                    <View
                        style={[styles.fieldCell, styles.borderRight, { flexDirection: "row", alignItems: "center" }]}
                    >
                        <Text style={[styles.smallLabel, { width: 70, marginLeft: 2 }]}>
                            {t("f280pdf.submittalFor", "Submittal is for: [!!]")}
                        </Text>
                        <View style={styles.selectionBoxContainer}>
                            <Checkbox
                                checked={!includeRoomByRoomResults}
                                label={t("f280pdf.wholeHouse", "Whole house [!!]")}
                            />
                            <Checkbox
                                checked={includeRoomByRoomResults}
                                label={t("f280pdf.roomByRoom", "Room by Room [!!]")}
                            />
                        </View>
                        <NumField>9</NumField>
                    </View>
                    <View style={[styles.fieldCell, { flexDirection: "row", alignItems: "center" }]}>
                        <Text style={[styles.smallLabel, { width: 70, textAlign: "center" }]}>
                            {t("f280pdf.units", "Units: [!!]")}
                        </Text>
                        <View style={styles.selectionBoxContainer}>
                            <Checkbox checked={units === "Imperial"} label={t("f280pdf.imperial", "Imperial [!!]")} />
                            <Checkbox checked={units === "Metric"} label={t("f280pdf.metric", "Metric [!!]")} />
                        </View>
                        <NumField>b</NumField>
                    </View>
                </Row>

                {/* Heating */}
                <SmallTitle>{t("f280pdf.heating", "HEATING [!!]")}</SmallTitle>
                <View
                    style={{
                        borderBottomWidth: 1,
                        borderColor: "#000",
                        borderStyle: "solid",
                        flexGrow: 1,
                        justifyContent: "space-around",
                    }}
                >
                    <View style={styles.textWithBoxContainer}>
                        <Text style={styles.textWithBox}>
                            {t("f280pdf.minHeatingCap", "Minimum Heating Capacity: [!!]")}
                        </Text>
                        <ValueBox value={parseFloat(heatLossValue).toFixed(0)} unit={heatUnit} />
                        <Text style={styles.smallLabel}>
                            {t("f280pdf.totalHeatLoss", "(total building heat loss as per 5.2.7) [!!]")}
                        </Text>
                        <NumField style={{ right: -6 }}>c</NumField>
                    </View>
                    <Clause num="5.3.1">
                        {t(
                            "f280pdf.clause5.3.1",
                            "The total heat output capacity of all heating systems installed in a building shall not be less than 100% of the total building heat loss as determined in Clause 5.2.7. [!!]"
                        )}
                    </Clause>
                    <Clause num="5.3.2">
                        {t(
                            "f280pdf.clause5.3.2",
                            "The combined heating delivery of the heating systems that serve a room or space shall not be less than 100% of the space heat loss , as determined in Clause 5.2.6. (If room by room submittal, see page 2 for individual space heating requirements) [!!]"
                        )}
                    </Clause>
                </View>

                {/* Cooling */}
                <SmallTitle>{t("f280pdf.cooling", "COOLING [!!]")}</SmallTitle>
                <View
                    style={{
                        borderBottomWidth: 1,
                        borderColor: "#000",
                        borderStyle: "solid",
                        flexGrow: 1,
                        justifyContent: "space-around",
                    }}
                >
                    <View style={[styles.textWithBoxContainer, { marginVertical: 5 }]}>
                        <Text style={[styles.textWithBox, { marginRight: "auto" }]}>
                            {t("f280pdf.coolingCap", "Cooling Capacity: [!!]")}
                        </Text>
                        <View style={styles.boxesContainer}>
                            <View style={styles.boxWithTextWrapper}>
                                <Text style={styles.smallLabel}>{t("f280pdf.nominal", "Nominal= [!!]")}</Text>
                                <ValueBox value={nominal.toFixed(0)} unit={heatUnit} rectStyle={{ width: 60 }} />
                                <NumField style={{ right: 18 }}>d</NumField>
                            </View>
                            <View style={styles.boxWithTextWrapper}>
                                <Text style={styles.smallLabel}>{t("f280pdf.min", "Minimum= [!!]")}</Text>
                                <ValueBox
                                    value={(nominal * 0.8).toFixed(0)}
                                    unit={heatUnit}
                                    rectStyle={{ width: 60 }}
                                />
                                <NumField style={{ right: 18 }}>e</NumField>
                            </View>
                            <View style={styles.boxWithTextWrapper}>
                                <Text style={styles.smallLabel}>{t("f280pdf.max", "Maximum= [!!]")}</Text>
                                <ValueBox
                                    value={(nominal * 1.25).toFixed(0)}
                                    unit={heatUnit}
                                    rectStyle={{ width: 60 }}
                                />
                                <NumField style={{ right: 18 }}>f</NumField>
                            </View>
                        </View>
                    </View>
                    <Clause num="6.3.2">
                        {t(
                            "f280pdf.clause6.3.2",
                            "Except as provided in Clause 6.3.3., the cooling system capacity shall not be less than 80% of the nominal cooling capacity for the building, as determined in Clause 6.3.1. In no case shall it be less than the nominal cooling capacity of the building minus 1800 W (0.51 tons) [!!]"
                        )}
                    </Clause>
                    <Clause num="6.3.3">
                        {t(
                            "f280pdf.clause6.3.3",
                            "Where the cooling system is added to an existing heating system, it's capacity in Watts shall not exceed 18 times the capacity of the air-handling capacity of the existing system in L/s. (Cooling capacity in Tons not more than 1.0 per 400 CFM of air handling capacity) [!!]"
                        )}
                    </Clause>
                    <Clause num="6.3.4">
                        {t(
                            "f280pdf.clause6.3.4",
                            "Except for ground-source and water source heat pumps used for cooling, and as permitted in Clause 6.3.5, the installed cooling capacity shall not exceed 125% of the nominal cooling capacity for the building, as determined in Clause 6.3.1. [!!]"
                        )}
                    </Clause>
                    <Clause num="6.3.5">
                        {t(
                            "f280pdf.clause6.3.5",
                            "If the nominal cooling system capacity for the building, as determined in Clause 6.3.1. is less than 6,000 W (1.7 tons), the installed cooling system capacity may exceed the nominal cooling system capacity for the building by up to 1750 W (0.49 tons). [!!]"
                        )}
                    </Clause>
                </View>

                {/* Attached documents */}
                <SmallTitle>{t("f280pdf.attachedDocs", "ATTACHED DOCUMENTS [!!]")}</SmallTitle>
                <Row>
                    <View
                        style={[
                            styles.fieldCell,
                            styles.borderRight,
                            {
                                flexDirection: "row",
                                alignItems: "center",
                                justifyContent: "space-around",
                                minHeight: 26,
                            },
                        ]}
                    >
                        <Checkbox
                            checked={designSummary}
                            label={t("f280pdf.designSummary", "Design Summary [!!]")}
                            wrapperStyle={{ justifyContent: "center" }}
                            num="g"
                        />
                        <Checkbox
                            checked={roomByRoomResults}
                            label={t("f280pdf.roomByRoomResults", "Room by Room Results [!!]")}
                            wrapperStyle={{ justifyContent: "center" }}
                            num="h"
                        />
                    </View>
                    <Field label={t("f280pdf.other", "Other: [!!]")} value={additionalDocsList || ""} num="i" />
                </Row>
                <Row>
                    <Field label={t("f280pdf.other", "Other: [!!]")} value="" num="i" />
                </Row>
                <Row>
                    <View style={[styles.cellSmallPadding, styles.fullFlex, { flexDirection: "row" }]}>
                        <Text style={[styles.fieldLabel, { width: 40 }]}>{t("f280pdf.notes", "Notes: [!!]")}</Text>
                        <View style={{ flex: 1 }}>
                            <View
                                style={{
                                    height: 18,
                                    borderBottomWidth: 1,
                                    borderColor: "#000",
                                    borderStyle: "solid",
                                    justifyContent: "center",
                                }}
                            >
                                <Text style={styles.fieldValue}>{notes || ""}</Text>
                            </View>
                            <View style={{ height: 18 }} />
                        </View>
                        <NumField>j</NumField>
                    </View>
                </Row>

                {/* Calculations performed by */}
                <SmallTitle>{t("f280pdf.calculationsBy", "CALCULATIONS PERFORMED BY [!!]")}</SmallTitle>
                {/* minHeight matters: the basis-0 field cells report ~zero natural height
                    to Yoga, so without a floor this row loses the grow split against the
                    heating/cooling sections and its content overflows onto the footer. */}
                <Row style={[styles.fillRow, { minHeight: 180 }]}>
                    <View style={styles.calcLeftPair}>
                        <View style={styles.columnA}>
                            <Field
                                label={t("f280pdf.name", "Name: [!!]")}
                                value={userName}
                                num="55"
                                containerStyle={styles.borderBottom}
                            />
                            <Field
                                label={t("f280pdf.company", "Company: [!!]")}
                                value={company}
                                num="56"
                                containerStyle={styles.borderBottom}
                            />
                            <Field
                                label={t("f280pdf.address", "Address: [!!]")}
                                value={companyAddress}
                                num="57"
                                containerStyle={styles.borderBottom}
                            />
                            <Field
                                label={t("f280pdf.city&Province", "City & Province: [!!]")}
                                value={userCityProvince}
                                num="58"
                                containerStyle={styles.borderBottom}
                            />
                            <Field
                                label={t("f280pdf.postalCode", "Postal code: [!!]")}
                                value={companyPostalCode}
                                num="59"
                                containerStyle={styles.borderBottom}
                            />
                            <Field
                                label={t("f280pdf.telephone", "Telephone: [!!]")}
                                value={companyPhone}
                                num="60"
                                containerStyle={styles.borderBottom}
                            />
                            <Field
                                label={t("f280pdf.fax", "Fax: [!!]")}
                                value={companyFax}
                                num="61"
                                containerStyle={styles.borderBottom}
                            />
                            <Field
                                label={t("f280pdf.email", "E-mail: [!!]")}
                                value={emailFromForm || email}
                                num="62"
                                labelStyle={{ width: 30 }}
                            />
                        </View>
                        <View style={styles.stampBox}>
                            <NumField>68</NumField>
                        </View>
                    </View>
                    <View style={styles.columnPlain}>
                        <View style={[styles.cellSmallPadding, styles.borderBottom, { minHeight: 60, padding: 6 }]}>
                            <Text style={{ fontSize: 6, lineHeight: 1.4 }}>
                                {t("f280pdf.attestation.1", "I,")}{" "}
                                <Text style={{ color: COLORS.blue, fontFamily: "Helvetica-Bold", fontSize: 7 }}>
                                    {userName}
                                </Text>{" "}
                                {t(
                                    "f280pdf.attestation.2",
                                    ", have reviewed and take responsibility for the design work described in this document & I am qualified in the appropriate categories. [!!]"
                                )}
                            </Text>
                            <NumField>63</NumField>
                        </View>
                        <Field
                            label={`${t("f280pdf.accredRef.1", "Accreditation [!!]", { num: 1 })}\n${t("f280pdf.accredRef.2", "Reference 1 [!!]", { num: 1 })}`}
                            value=""
                            num="64"
                            containerStyle={[styles.borderBottom, { minHeight: 26 }]}
                        />
                        <Field
                            label={`${t("f280pdf.accredRef.1", "Accreditation [!!]", { num: 2 })}\n${t("f280pdf.accredRef.2", "Reference 2 [!!]", { num: 2 })}`}
                            value=""
                            num="65"
                            containerStyle={[styles.borderBottom, { minHeight: 26 }]}
                        />
                        <Field
                            label={t("f280pdf.issuedFor", "Issued Date & Purpose: [!!]")}
                            value=""
                            num="66"
                            containerStyle={styles.borderBottom}
                        />
                        <Field
                            label={t("f280pdf.reIssuedFor", "Re-Issued Date & Purpose: [!!]")}
                            value=""
                            num="67"
                            containerStyle={styles.borderBottom}
                        />
                        <View
                            style={[
                                styles.cellSmallPadding,
                                { alignItems: "center", justifyContent: "center", minHeight: 16 },
                            ]}
                        >
                            <Text style={{ fontFamily: "Helvetica-Bold", fontSize: 7 }}>
                                {t("f280pdf.page", `Page: 1 of 2 [!!]`, { current: 1, total: totalPages })}
                            </Text>
                        </View>
                    </View>
                </Row>

                <Footer
                    footnote={t("f280pdf.footnote", `Version ${APP_VERSION}. Contact: info@voltaresearch.org [!!]`, {
                        version: APP_VERSION,
                    })}
                    num="69"
                />
            </View>
        </Page>
    );
};

export default F280FirstPage;
